// Field values for the report engine. Missing is not zero: every amount and date says which state it is in.
import { D, type Dec, money } from "../money.ts";

/**
 * A money amount as entered or read from a document. Decimals travel as strings so the year file is plain JSON.
 * - unknown: nobody has told us yet
 * - blank: the document shows the box but leaves it empty
 * - not_applicable: the user said this doesn't apply to them
 * - unreadable: a parser saw something it couldn't read; `raw` keeps what it saw
 */
export type Amount =
  | { state: "unknown" }
  | { state: "blank" }
  | { state: "not_applicable" }
  | { state: "unreadable"; raw: string }
  | { state: "value"; value: string; raw?: string };

export const UNKNOWN: Amount = { state: "unknown" };
export const BLANK: Amount = { state: "blank" };
export const NOT_APPLICABLE: Amount = { state: "not_applicable" };

/** Parse a user- or document-supplied money string into an Amount. Ambiguous text stays unreadable. */
export function amountFrom(raw: string): Amount {
  const s = raw.trim();
  if (s === "") return BLANK;
  let value: string;
  try {
    value = money(s).toFixed();
  } catch {
    return { state: "unreadable", raw: s };
  }
  return value === s ? { state: "value", value } : { state: "value", value, raw: s };
}

export function amt(value: string): Amount {
  return { state: "value", value: new D(value).toFixed() };
}

/** The decimal, or null for every state that isn't a known value. */
export function known(a: Amount | undefined): Dec | null {
  return a && a.state === "value" ? new D(a.value) : null;
}

/** A box the form leaves blank is zero for arithmetic; unknown and unreadable are not. */
export function boxValue(a: Amount | undefined): Dec | null {
  if (!a) return null;
  if (a.state === "value") return new D(a.value);
  if (a.state === "blank") return new D(0);
  return null;
}

/** Calendar date as YYYY-MM-DD. Date-only values never become timestamps. */
export type IsoDate = string;

export function isIsoDate(s: unknown): s is IsoDate {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1) return false;
  const days = [31, y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return d <= days[m - 1]!;
}

export function yearOf(date: IsoDate): number {
  return Number(date.slice(0, 4));
}
